import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.OrdinaryRiderCode[] = [
  {
    code: 'IHR1',
    displayName: 'IHR1',
    units: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  },
  {
    code: 'SAR1',
    displayName: 'SAR1',
    units: [1, 2, 3, 4],
  },
  {
    code: 'IPBR1',
    displayName: 'IPBR1',
    units: [1, 2, 3, 4],
  },
  {
    code: 'OEAR1',
    displayName: 'OEAR1',
    units: [1, 2],
  },
  {
    code: 'OPBR1',
    displayName: 'OPBR1',
    units: [1, 2, 3],
  },
  {
    code: 'AHNR',
    displayName: 'AHNR',
    units: [1],
  },
  {
    code: 'TR1',
    displayName: 'TR1',
    units: [1],
  },
  {
    code: 'ADIR1',
    displayName: 'ADIR1',
    units: [1, 2, 3, 4, 5, 6],
  },
  {
    code: 'SDIR1',
    displayName: 'SDIR1',
    units: [1, 2, 3, 4, 5, 6],
  },
];

type ShopProps = {
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  baseUnit: number;
  selectCGI: boolean;
  section125: boolean;
  riderInformation: RI.default;
};

export class InvalidOrdShop {
  public readonly key = 'InvalidOrdShop';

  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly baseUnit: number,
    public readonly selectCGI: boolean,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default
  ) {}
}

export class ValidOrdShop {
  public static createApp({
    planType,
    premium,
    calculatedPremium,
    cwa,
    baseUnit,
    selectCGI,
    section125,
    riderInformation,
  }: ShopProps) {
    return this.create(planType, premium, calculatedPremium, cwa, baseUnit, selectCGI, section125, riderInformation);
  }

  public static create(
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    baseUnit: number,
    selectCGI: boolean,
    section125: boolean,
    riderInformation: RI.default
  ) {
    return isNonEmptyString(planType)
      ? new ValidOrdShop(planType, premium, calculatedPremium, cwa, baseUnit, selectCGI, section125, riderInformation)
      : new InvalidOrdShop(
          planType,
          premium,
          calculatedPremium,
          cwa,
          baseUnit,
          selectCGI,
          section125,
          riderInformation
        );
  }

  public readonly key = 'ValidOrdShop';
  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly baseUnit: number,
    public readonly selectCGI: boolean,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default
  ) {}
}

export type OrdShop = InvalidOrdShop | ValidOrdShop;

export type OrdShopOptions<TReturn> = ToPartialMatchOptions<OrdShop, 'key', TReturn>;

export const matchOrdShop = <TReturn>(options: OrdShopOptions<TReturn>) => (app: OrdShop): TReturn => {
  switch (app.key) {
    case 'InvalidOrdShop':
      return options.InvalidOrdShop ? options.InvalidOrdShop(app) : options.default(app);
    case 'ValidOrdShop':
      return options.ValidOrdShop ? options.ValidOrdShop(app) : options.default(app);
  }
};

export const EmptyOrdShop = new InvalidOrdShop(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  -1,
  false,
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes)
);

export const changePlanType = (planType: string) => (app: OrdShop) =>
  ValidOrdShop.createApp({
    ...app,
    planType: NonEmptyString.create(planType),
    calculatedPremium: new EmptyString(),
    cwa: new EmptyString(),
  });
export const changePremium = (premium: string) => (app: OrdShop) =>
  ValidOrdShop.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdShop) =>
  ValidOrdShop.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: OrdShop) =>
  ValidOrdShop.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeBaseUnit = (baseUnit: number) => (app: OrdShop) => ValidOrdShop.createApp({...app, baseUnit});
export const changeSection = (section: boolean) => (app: OrdShop) =>
  ValidOrdShop.createApp({...app, section125: section});
export const changeCGI = (cgi: boolean) => (app: OrdShop) => ValidOrdShop.createApp({...app, selectCGI: cgi});

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: OrdShop) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidOrdShop.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const getAllRiderCodes = () => riderCodes;
